import { useEffect, useState, type ReactElement } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchSearch } from '../../api/admin.js';
import { formatDateTime, shortId } from '../../lib/format.js';

export function SearchPage(): ReactElement {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQ = searchParams.get('q') ?? '';
  const [q, setQ] = useState(initialQ);

  useEffect(() => {
    setQ(searchParams.get('q') ?? '');
  }, [searchParams]);

  const query = useQuery({
    queryKey: ['admin', 'search', initialQ],
    queryFn: () => fetchSearch(initialQ),
    enabled: initialQ.trim().length >= 2,
  });

  function onSubmit(e: React.FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    const next = q.trim();
    if (next.length < 2) return;
    setSearchParams({ q: next });
  }

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">Search</h1>
          <p className="page-sub">Por email, session_id, trace_id, provider_request_id.</p>
        </div>
      </div>
      <form className="filterbar" onSubmit={onSubmit}>
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="email o UUID…"
          style={{ minWidth: 320 }}
          data-testid="search-input"
        />
        <button type="submit" className="btn primary" data-testid="search-submit">
          Buscar
        </button>
      </form>
      {initialQ.trim().length < 2 ? (
        <div className="loading">Escribí al menos 2 caracteres.</div>
      ) : query.isPending ? (
        <div className="loading">Buscando…</div>
      ) : query.isError ? (
        <div className="loading">Error: {(query.error).message}</div>
      ) : (
        <div className="pad-7" data-testid="search-results">
          <section className="card" style={{ marginBottom: 16 }}>
            <header className="card-head">
              <h3 className="card-title">
                Users <small>{query.data.users.length}</small>
              </h3>
            </header>
            {query.data.users.length === 0 ? (
              <div className="empty">Sin matches</div>
            ) : (
              <div className="grid-list">
                {query.data.users.map((u) => (
                  <Link key={u.id} to={`/users/${u.id}`} className="grid-list-row">
                    <span>{u.email}</span>
                    <span className="mono tt-tert">{u.role}</span>
                  </Link>
                ))}
              </div>
            )}
          </section>
          <section className="card" style={{ marginBottom: 16 }}>
            <header className="card-head">
              <h3 className="card-title">
                Sessions <small>{query.data.sessions.length}</small>
              </h3>
            </header>
            {query.data.sessions.length === 0 ? (
              <div className="empty">Sin matches</div>
            ) : (
              <div className="grid-list">
                {query.data.sessions.map((s) => (
                  <Link
                    key={s.id}
                    to={`/sessions/${s.id}`}
                    className="grid-list-row"
                    data-testid={`search-session-${s.id}`}
                  >
                    <span>
                      {s.userEmail} · {s.moduleId ?? '—'}
                    </span>
                    <span className="mono tt-tert">
                      {shortId(s.id)} · {formatDateTime(s.startedAt)}
                    </span>
                  </Link>
                ))}
              </div>
            )}
          </section>
          <section className="card">
            <header className="card-head">
              <h3 className="card-title">
                Traces <small>{query.data.traces.length}</small>
              </h3>
            </header>
            {query.data.traces.length === 0 ? (
              <div className="empty">Sin matches</div>
            ) : (
              <div className="grid-list">
                {query.data.traces.map((t) => (
                  <Link
                    key={t.traceId}
                    to={t.firstSessionId ? `/sessions/${t.firstSessionId}` : '#'}
                    className="grid-list-row"
                  >
                    <span>trace {shortId(t.traceId)}</span>
                    <span className="mono tt-tert">{t.spanCount} spans</span>
                  </Link>
                ))}
              </div>
            )}
          </section>
        </div>
      )}
    </>
  );
}
